import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { TextInput, Select } from "flowbite-react";
import { Spinner } from "../components/atoms/Spinner";
import { StatusBadge } from "../components/atoms/StatusBadge";
import useAuth from "../hooks/useAuth";
import HttpService from "../services/http";
import { CustomButton } from "../components/atoms/CustomButton";
import AiEngineModels from "../components/pages/dashboard/AiEngineModels";
import CollapsibleHowItWorks from "../components/common/CollapsibleHowItWorks";
import { HiOutlineCube, HiOutlineSearch } from "react-icons/hi";
import { EmptyState } from "../components/atoms/EmptyState";

type Health = {
  status: "ok" | "degraded" | "down" | string;
  db?: boolean;
  pgvector?: boolean;
  version?: string | null;
  models?: { llm?: string; embed?: string; dim?: number };
  embedding_configs?: Array<{
    id: number;
    provider: string;
    model: string;
    dim: number;
    deprecated_at?: string | null;
  }>;
  counts?: {
    documents?: number | null;
    document_chunks?: number | null;
    entities_with_catalog?: number | null;
    global_native_keys?: number | null;
    search_logs?: number | null;
  };
  error?: string;
};

type SearchLog = {
  id: number | string;
  entity_id: string;
  user_id?: string | null;
  question: string;
  intent?: string | null;
  umd_path?: string | null;
  reason?: string | null;
  result_count?: number | null;
  rag_answer?: string | null;
  created_at: string;
};

type LogsPayload = {
  logs: SearchLog[];
  total: number;
  limit: number;
  offset: number;
};

const PAGE_SIZE = 25;

const AiEngineMonitoring: React.FC = () => {
  const { customer_identification, user } = useAuth();
  const base = `/${customer_identification}/${user?.entity_identification}/system/ai-engine`;

  const [health, setHealth] = useState<Health | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);

  const [logs, setLogs] = useState<SearchLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [logsLoading, setLogsLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState("");
  const [appliedFilter, setAppliedFilter] = useState("");
  const [sort, setSort] = useState<"desc" | "asc">("desc");

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const res = await HttpService.get<{ element: Health }>(`${base}/health`);
      if (!aliveRef.current) return;
      setHealth(res.data?.element ?? { status: "down" });
    } catch {
      if (aliveRef.current) setHealth({ status: "down" });
    } finally {
      if (aliveRef.current) setHealthLoading(false);
    }
  }, [base]);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(PAGE_SIZE));
      params.set("sort", sort);
      if (appliedFilter)
        params.set("target_entity_identification", appliedFilter);
      const res = await HttpService.get<{ element: LogsPayload }>(
        `${base}/search-logs?${params.toString()}`
      );
      if (!aliveRef.current) return;
      const el = res.data?.element;
      setLogs(el?.logs ?? []);
      setTotal(el?.total ?? 0);
    } catch {
      if (!aliveRef.current) return;
      setLogs([]);
      setTotal(0);
    } finally {
      if (aliveRef.current) setLogsLoading(false);
    }
  }, [base, page, appliedFilter, sort]);

  useEffect(() => {
    if (user?.entity_identification) loadHealth();
  }, [user?.entity_identification, loadHealth]);

  useEffect(() => {
    if (user?.entity_identification) loadLogs();
  }, [user?.entity_identification, loadLogs]);

  const applyFilter = () => {
    setPage(1);
    setAppliedFilter(entityFilter.trim());
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="ai-engine-monitoring-page">
      <Helmet>
        <title>AI Engine Monitoring | Super User Manager</title>
      </Helmet>

      <div className="container mx-auto max-w-6xl px-4 py-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900">
              AI Engine Monitoring
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Service health, model catalogue and recent searches
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CustomButton
              variant="secondary"
              title="Refresh"
              onClick={() => {
                loadHealth();
                loadLogs();
              }}
            />
          </div>
        </div>

        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-sm font-semibold text-gray-900">Insights</div>

          {healthLoading ? (
            <div className="flex justify-center py-6">
              <Spinner size="md" />
            </div>
          ) : health?.status === "down" ? (
            <p className="mt-3 text-sm text-red-600">
              AI Engine is unreachable. The search and embedding features may be
              unavailable.
            </p>
          ) : (
            <>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Kpi label="Documents" value={health?.counts?.documents} />
                <Kpi label="Chunks" value={health?.counts?.document_chunks} />
                <Kpi
                  label="Entities with catalog"
                  value={health?.counts?.entities_with_catalog}
                />
              </div>
              <dl className="mt-4 grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                <dt className="text-gray-500">LLM</dt>
                <dd className="truncate text-gray-700">
                  {health?.models?.llm || "—"}
                </dd>
                <dt className="text-gray-500">Embedding</dt>
                <dd className="truncate text-gray-700">
                  {health?.models?.embed || "—"}
                  {health?.models?.dim ? ` (${health.models.dim})` : ""}
                </dd>
              </dl>
            </>
          )}
        </div>

        <CollapsibleHowItWorks
          collapsible
          defaultOpen={false}
          size="lg"
          title="Models"
          icon={<HiOutlineCube className="text-gray-500" size={20} />}
          className="mb-6"
        >
          <AiEngineModels base={base} />
        </CollapsibleHowItWorks>

        <CollapsibleHowItWorks
          collapsible
          defaultOpen={false}
          size="lg"
          title={`Recent searches (${total})`}
          icon={<HiOutlineSearch className="text-gray-500" size={20} />}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            <div className="flex items-center gap-2">
              <Select
                sizing="sm"
                value={sort}
                onChange={(e) => {
                  setPage(1);
                  setSort(e.target.value === "asc" ? "asc" : "desc");
                }}
              >
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </Select>
              <TextInput
                sizing="sm"
                placeholder="Filter by entity ID…"
                value={entityFilter}
                onChange={(e) => setEntityFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilter()}
              />
              <CustomButton
                variant="secondary"
                icon="documentSearch"
                onClick={applyFilter}
              />
            </div>
          </div>

          {logsLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : logs.length === 0 ? (
            <EmptyState
              icon={<HiOutlineSearch size={24} />}
              title="No logged searches."
            />
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                    <th className="py-2 pr-3 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Entity</th>
                    <th className="w-full px-3 py-2 font-medium">Question</th>
                    <th className="px-3 py-2 font-medium">Routing</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map((l) => (
                    <tr key={l.id} className="align-top">
                      <td className="whitespace-nowrap py-2 pr-3 text-xs text-gray-500">
                        {new Date(l.created_at).toLocaleString()}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                        {l.entity_id}
                      </td>
                      <td className="w-full max-w-md px-3 py-2">
                        <div className="text-gray-700">{l.question}</div>
                        {l.rag_answer && (
                          <div className="mt-0.5 line-clamp-2 text-xs text-gray-500">
                            {l.rag_answer}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <StatusBadge
                          color={l.reason ? "warning" : "gray"}
                          size="xs"
                        >
                          {l.intent || "—"}
                        </StatusBadge>
                        <div className="mt-0.5 text-xs text-gray-500">
                          {l.reason ? l.reason : l.umd_path || ""}
                          {typeof l.result_count === "number"
                            ? ` · ${l.result_count}`
                            : ""}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > PAGE_SIZE && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <CustomButton
                variant="secondary"
                icon="arrowLeft"
                title="Previous"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              />
              <span className="text-xs text-gray-500">
                Page {page} of {totalPages}
              </span>
              <CustomButton
                variant="secondary"
                icon="arrowRight"
                title="Next"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              />
            </div>
          )}
        </CollapsibleHowItWorks>
      </div>
    </div>
  );
};

const Kpi: React.FC<{ label: string; value?: number | null }> = ({
  label,
  value,
}) => (
  <div className="rounded-lg border border-gray-200 px-3 py-2">
    <div className="text-lg font-semibold leading-none text-gray-900 tabular-nums">
      {value ?? "—"}
    </div>
    <div className="mt-1 text-xs text-gray-500">{label}</div>
  </div>
);

export default AiEngineMonitoring;
